import React, { useEffect, useState } from "react";
import Header from "../components/Admin/header/Header";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  Grid,
  IconButton,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DatePicker } from "@mui/x-date-pickers";
import { CheckBox, CheckBoxOutlineBlank } from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { DOCTORS_UNIT_SETTING } from "../_mock/mock";
import { upsertPolicy } from "../utils/policyMasterStorage";

function emptyCriteria() {
  return {
    age: false,
    gender: false,
    visitType: false,
    opd: false,
    ipd: false,
    storeBill: false,
    units: false,
    doctors: false,
    location: false,
  };
}

function buildInitialState() {
  return {
    policyName: "",
    type: "",
    relief: "",
    schemeValidity: "",
    schemeFrom: null,
    schemeTo: null,
    active: true,
    criteria: emptyCriteria(),
    selectedUnits: [],
  };
}

export default function MasterPolicyAdd() {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState(buildInitialState);
  const [editId, setEditId] = useState(null);

  useEffect(() => {
    const p = location.state?.policy;
    if (!p) {
      setFormData(buildInitialState());
      setEditId(null);
      return;
    }
    setEditId(p.id ?? p._id ?? null);
    setFormData({
      policyName: p.policyName ?? p.name ?? "",
      type: p.type ?? "",
      relief: p.relief ?? "",
      schemeValidity: p.schemeValidity ?? "",
      schemeFrom: p.schemeFrom ? dayjs(p.schemeFrom) : null,
      schemeTo: p.schemeTo ? dayjs(p.schemeTo) : null,
      active: p.active === true || p.active === "Active",
      criteria: { ...emptyCriteria(), ...(p.criteria || {}) },
      selectedUnits: Array.isArray(p.selectedUnits) ? p.selectedUnits : [],
    });
  }, [location.state]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.policyName?.trim()) {
      toast.error("Policy name is required.");
      return;
    }
    if (!formData.schemeFrom || !formData.schemeTo) {
      toast.error("Scheme from and to dates are required.");
      return;
    }
    if (formData.schemeTo.isBefore(formData.schemeFrom, "day")) {
      toast.error("Scheme end date must be on or after the start date.");
      return;
    }

    const typePart = (formData.type || "").trim();
    const validityPart = (formData.schemeValidity || "").trim();
    const record = {
      id: editId != null ? editId : Date.now(),
      name: formData.policyName.trim(),
      scheme: typePart || validityPart || "-",
      active: formData.active,
      policyName: formData.policyName.trim(),
      type: formData.type.trim(),
      relief: formData.relief.trim(),
      schemeValidity: formData.schemeValidity.trim(),
      schemeFrom: formData.schemeFrom.format("YYYY-MM-DD"),
      schemeTo: formData.schemeTo.format("YYYY-MM-DD"),
      criteria: { ...formData.criteria },
      selectedUnits: formData.selectedUnits,
    };

    upsertPolicy(record);
    toast.success(editId != null ? "Policy updated." : "Policy saved.");
    navigate("/master/policy", { replace: true, state: {} });
  };

  const setCriterion = (key, checked) => {
    setFormData((prev) => {
      const nextCriteria = { ...prev.criteria, [key]: checked };
      const next = { ...prev, criteria: nextCriteria };
      if (key === "units" && !checked) next.selectedUnits = [];
      return next;
    });
  };

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box>
          <Sidebar />
        </Box>

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ width: "100%", pr: "90px", py: "20px" }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: "20px",
            }}
          >
            <Typography sx={{ color: "rgba(93, 106, 130, 1)", fontSize: 20 }}>
              {editId != null ? "Edit Policy" : "Add Policy"}
            </Typography>
            <IconButton onClick={() => navigate(-1)} aria-label="Close">
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ mb: "30px" }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  name="policyName"
                  label="Policy Name"
                  required
                  fullWidth
                  sx={{ bgcolor: "white" }}
                  value={formData.policyName}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, policyName: e.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  name="type"
                  label="Type"
                  required
                  fullWidth
                  sx={{ bgcolor: "white" }}
                  value={formData.type}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, type: e.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  name="relief"
                  label="Relief"
                  required
                  fullWidth
                  sx={{ bgcolor: "white" }}
                  value={formData.relief}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, relief: e.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  name="schemeValidity"
                  label="Scheme Validity"
                  required
                  fullWidth
                  sx={{ bgcolor: "white" }}
                  value={formData.schemeValidity}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, schemeValidity: e.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      label="Scheme From"
                      value={formData.schemeFrom}
                      onChange={(v) =>
                        setFormData((p) => ({ ...p, schemeFrom: v }))
                      }
                      slotProps={{
                        textField: { required: true, fullWidth: true },
                      }}
                      sx={{ bgcolor: "white", width: "100%" }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      label="Scheme To"
                      value={formData.schemeTo}
                      onChange={(v) =>
                        setFormData((p) => ({ ...p, schemeTo: v }))
                      }
                      slotProps={{
                        textField: { required: true, fullWidth: true },
                      }}
                      sx={{ bgcolor: "white", width: "100%" }}
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} sm={6} lg={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.active}
                      onChange={(e) =>
                        setFormData((p) => ({ ...p, active: e.target.checked }))
                      }
                    />
                  }
                  sx={{ height: "57px" }}
                  label="Active"
                />
              </Grid>
            </Grid>
          </Box>

          <Box
            sx={{
              border: "1px solid rgba(223, 232, 239, 1)",
              borderRadius: "8px",
              mb: "30px",
              p: "20px",
            }}
          >
            <TableContainer component={Paper}>
              <Table>
                <TableHead sx={{ bgcolor: "#EAEAEA" }}>
                  <TableRow>
                    <TableCell>Criteria</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <Box sx={{ p: "15px" }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={formData.criteria.age}
                                  onChange={(e) =>
                                    setCriterion("age", e.target.checked)
                                  }
                                />
                              }
                              sx={{ height: "57px" }}
                              label="Age"
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={formData.criteria.gender}
                                  onChange={(e) =>
                                    setCriterion("gender", e.target.checked)
                                  }
                                />
                              }
                              sx={{ height: "57px" }}
                              label="Gender"
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={formData.criteria.visitType}
                                  onChange={(e) =>
                                    setCriterion("visitType", e.target.checked)
                                  }
                                />
                              }
                              sx={{ height: "57px" }}
                              label="Visit Type"
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={formData.criteria.opd}
                                  onChange={(e) =>
                                    setCriterion("opd", e.target.checked)
                                  }
                                />
                              }
                              sx={{ height: "57px" }}
                              label="OPD"
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={formData.criteria.ipd}
                                  onChange={(e) =>
                                    setCriterion("ipd", e.target.checked)
                                  }
                                />
                              }
                              sx={{ height: "57px" }}
                              label="IPD"
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={formData.criteria.storeBill}
                                  onChange={(e) =>
                                    setCriterion("storeBill", e.target.checked)
                                  }
                                />
                              }
                              sx={{ height: "57px" }}
                              label="Store Bill"
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={formData.criteria.units}
                                  onChange={(e) =>
                                    setCriterion("units", e.target.checked)
                                  }
                                />
                              }
                              sx={{ height: "57px" }}
                              label="Unit(s)"
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Autocomplete
                              multiple
                              disabled={!formData.criteria.units}
                              disableCloseOnSelect
                              options={DOCTORS_UNIT_SETTING || []}
                              value={formData.selectedUnits}
                              onChange={(_, v) =>
                                setFormData((p) => ({
                                  ...p,
                                  selectedUnits: v,
                                }))
                              }
                              getOptionLabel={(option) =>
                                option?.doctorName || ""
                              }
                              isOptionEqualToValue={(a, b) => a?.id === b?.id}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Select units"
                                  placeholder={
                                    formData.criteria.units
                                      ? "Search"
                                      : "Enable Unit(s) first"
                                  }
                                  fullWidth
                                />
                              )}
                              renderOption={(props, option, { selected }) => (
                                <li {...props}>
                                  <Checkbox
                                    icon={
                                      <CheckBoxOutlineBlank fontSize="small" />
                                    }
                                    checkedIcon={
                                      <CheckBox fontSize="small" />
                                    }
                                    checked={selected}
                                    sx={{ mr: 1 }}
                                  />
                                  {option?.doctorName || ""}
                                </li>
                              )}
                              renderTags={(selected, getTagProps) =>
                                selected?.map((option, index) => (
                                  <Chip
                                    label={option?.doctorName}
                                    size="small"
                                    {...getTagProps({ index })}
                                    key={option?.id}
                                  />
                                ))
                              }
                              sx={{ minWidth: "200px" }}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={formData.criteria.doctors}
                                  onChange={(e) =>
                                    setCriterion("doctors", e.target.checked)
                                  }
                                />
                              }
                              sx={{ height: "57px" }}
                              label="Doctor(s)"
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={formData.criteria.location}
                                  onChange={(e) =>
                                    setCriterion("location", e.target.checked)
                                  }
                                />
                              }
                              sx={{ height: "57px" }}
                              label="Location"
                            />
                          </Grid>
                        </Grid>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "center", mt: "20px" }}>
            <Button
              variant="contained"
              type="submit"
              sx={{
                textTransform: "none",
                backgroundColor: "rgba(1, 121, 176, 1)",
                width: "150px",
              }}
            >
              Submit
            </Button>
          </Box>
        </Box>
      </Box>
    </Container>
  );
}
